"use strict";
/**
 * Heading parsing for the use-case report markdown.
 *
 * Recognises both ATX (`#`, `##`) and Setext (`===` / `---` underline)
 * heading styles, produces stable url-safe slugs, and slices the source
 * markdown into anchored sections that the reader UI can render and
 * deep-link into.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.splitMarkdownIntoSections = exports.extractMarkdownHeadings = exports.slugifyHeading = void 0;
var markdown_text_1 = require("./markdown-text");
var sanitizeHeadingText = function (value) {
    return value
        .replace(/\\([\\`*_{}[\]()#+\-.!|>])/g, '$1')
        .replace(/[*_`]/g, '')
        .replace(/<[^>]+>/g, '')
        .trim();
};
/**
 * Create a stable, searchable id for a heading.
 */
exports.slugifyHeading = function (value) {
    return value
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
};
var parseMarkdownHeading = function (lines, index) {
    var currentLine = lines[index] || '';
    var nextLine = lines[index + 1] || '';
    var atxMatch = currentLine.match(/^(#{1,2})\s+(.*)$/);
    if (atxMatch) {
        var level = atxMatch[1].length;
        var text = sanitizeHeadingText(atxMatch[2]);
        return {
            level: level,
            text: text,
            consumedLines: 1,
            normalizedHeadingLine: '#'.repeat(level) + " " + text,
        };
    }
    if (!currentLine.trim()) {
        return null;
    }
    if (/^={3,}\s*$/.test(nextLine)) {
        var text = sanitizeHeadingText(currentLine);
        return { level: 1, text: text, consumedLines: 2, normalizedHeadingLine: "# " + text };
    }
    if (/^-{3,}\s*$/.test(nextLine)) {
        var text = sanitizeHeadingText(currentLine);
        return { level: 2, text: text, consumedLines: 2, normalizedHeadingLine: "## " + text };
    }
    return null;
};
/**
 * Extract first- and second-level markdown headings, deduplicating slugs
 * so the in-page anchors remain unique.
 */
exports.extractMarkdownHeadings = function (markdown, prefix) {
    var headings = [];
    var lines = markdown_text_1.normalizeReportText(markdown).split('\n');
    var usedIds = {};
    var parentId = '';
    for (var index = 0; index < lines.length; index += 1) {
        var parsed = parseMarkdownHeading(lines, index);
        if (!parsed) {
            continue;
        }
        var baseId = exports.slugifyHeading(prefix + "-" + parsed.text) || prefix + "-section";
        var duplicateCount = usedIds[baseId] || 0;
        var id = duplicateCount > 0 ? baseId + "-" + (duplicateCount + 1) : baseId;
        usedIds[baseId] = duplicateCount + 1;
        var heading = { id: id, level: parsed.level, text: parsed.text };
        if (parsed.level === 1) {
            parentId = id;
        }
        else if (parentId) {
            heading.parentId = parentId;
        }
        headings.push(heading);
        index += parsed.consumedLines - 1;
    }
    return headings;
};
/**
 * Split markdown into anchored sections keyed by heading id (matching the
 * order returned by {@link extractMarkdownHeadings}). When the source has
 * no headings, returns a single `'content'` section so the reader still
 * has something to render.
 */
exports.splitMarkdownIntoSections = function (markdown, headings) {
    var normalizedMarkdown = markdown_text_1.normalizeReportText(markdown);
    var lines = normalizedMarkdown.split('\n');
    var sections = [];
    var currentSectionLines = [];
    var headingIndex = -1;
    var commitSection = function () {
        if (headingIndex >= 0 && currentSectionLines.length > 0) {
            sections.push({
                id: headings[headingIndex].id,
                markdown: currentSectionLines.join('\n').trim(),
            });
        }
    };
    for (var index = 0; index < lines.length; index += 1) {
        var parsed = parseMarkdownHeading(lines, index);
        if (parsed) {
            commitSection();
            headingIndex += 1;
            currentSectionLines = [parsed.normalizedHeadingLine];
            index += parsed.consumedLines - 1;
            continue;
        }
        if (headingIndex >= 0) {
            currentSectionLines.push(lines[index]);
        }
    }
    commitSection();
    if (sections.length === 0 && normalizedMarkdown) {
        return [{ id: 'content', markdown: normalizedMarkdown }];
    }
    return sections;
};
